import { useMemo, useState } from 'react';
import {
  Button,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  Box,
  Dialog,
  useMediaQuery,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { AdvisorInterests } from '../../services/utils/types';

const useStyles = () => ({
  container: {
    borderRadius: { xxs: 0, xs: '10px' },
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
    maxWidth: '600px',
    padding: {
      xxs: '48px 0px 0px',
      xs: '30px',
    },
  },
  textHeadline: {
    fontWeight: '700',
    fontSize: '28px',
    padding: '8px 10px',
    color: '#232B35',
    textAlign: 'center',
  },
  textContent: {
    fontWeight: '500',
    fontSize: '13px',
    padding: '8px 10px',
    color: '#646D7A',
    padding: '0px',
    mb: '8px',
  },
  dialogActions: {
    width: '100%',
    marginTop: '8px',
    padding: '8px 12px',
  },
  mainButton: {
    width: '100%',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    height: '45px',
    borderRadius: '12px',
  },
});

export default function CustomInterestsModal(props) {
  const {
    open,
    setOpen,
    customInterests,
    setCustomInterests,
    interestsSelected,
    setInterestsSelected,
    unavailableOptions = [],
    setUnavailableOptions = () => {},
    setErrorSnackbar = () => {},
  } = props;
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles();
  const [localInterests, setLocalInterests] = useState(['', '', '']);
  const [interestErrors, setInterestErrors] = useState([]);
  const [isInviteButtonDisabled, setIsInviteButtonDisabled] = useState(true);
  const lowerCasedInterests = useMemo(
    () => [
      ...customInterests.map((interest) => interest.toLowerCase()),
      ...AdvisorInterests.map((interest) => interest.toLowerCase()),
      ...unavailableOptions.map((interest) => interest.toLowerCase()),
      ...localInterests.map((interest) => interest.toLowerCase()).filter((interest) => interest !== ''),
    ],
    [customInterests, localInterests]
  );

  const handleAddMoreInterests = () => {
    setLocalInterests((prev) => [...prev, '']);
  };

  function handleClose() {
    setOpen(false);
    setLocalInterests(['', '', '']);
    setInterestErrors([]);
    setIsInviteButtonDisabled(true);
  }

  const validateInterest = (interest) => {
    if (interest == "") {
      return false;
    }
    return (
      interest.length < 26 &&
      lowerCasedInterests.filter((i) => i === interest.toLowerCase()).length < 2
    );
  };

  const updateInterests = (index, value) => {
    const newInterests = [...localInterests];
    newInterests[index] = value;
    setLocalInterests(newInterests);

    if (interestErrors[index]) {
      const newErrors = [...interestErrors];
      newErrors[index] = !validateInterest(value) && value !== '';
      setInterestErrors(newErrors);
    }

    setIsInviteButtonDisabled(
      !newInterests.some(validateInterest) || interestErrors.includes(true)
    );
  };

  const handleBlur = () => {
    const newErrors = localInterests.map(
      (email) => !validateInterest(email) && email !== ''
    );
    setInterestErrors(newErrors);
    setIsInviteButtonDisabled(
      !localInterests.some(validateInterest) || newErrors.includes(true)
    );
  };

  const handleAdd = async () => {
    const capitalizeWords = (str) =>
      str
        .trim()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
    const validInterests = localInterests
      .filter((interest) => validateInterest(interest))
      .map(capitalizeWords);

    if (validInterests.length + interestsSelected.length > 15) {
      setErrorSnackbar(true);
      return;
    }
    setCustomInterests((prev) => [...prev, ...validInterests]);
    setInterestsSelected((prev) => [...prev, ...validInterests]);
    setUnavailableOptions((prev) => [...prev, ...validInterests]);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isXS}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: classes.container,
      }}
    >
      <Close
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: '12px',
          top: '12px',
          color: '#4D586F',
          cursor: 'pointer',
        }}
      />

      <DialogTitle
        id="alert-dialog-title"
        display="flex"
        justifyContent="center"
        sx={classes.textHeadline}
      >
        Add Your Interests!
      </DialogTitle>
      <DialogContent sx={{ padding: '8px 12px' }}>
        <Typography sx={classes.textContent}>
          Limit your interests to 25 characters
        </Typography>
        <Box sx={{ height: '210px', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'hidden',
              paddingRight: '10px',
              height: '100%',
              width: '100%',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F8F8F8',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '5px',
                backgroundColor: '#C7C7C7',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#B7B7B7',
              },
              '&::-webkit-scrollbar-thumb:active': {
                backgroundColor: '#B7B7B7',
                boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
              },
              '&:hover': {
                overflowY: 'auto',
              },
            }}
          >
            {localInterests.map((email, index) => (
              <TextField
                variant="outlined"
                key={index}
                placeholder="Type interest here"
                fullWidth
                value={email}
                onChange={(evt) => updateInterests(index, evt.target.value)}
                onBlur={handleBlur}
                error={interestErrors[index]}
                helperText={
                  interestErrors[index] ? 'Duplicated Interest' : ''
                }
                inputProps={{
                  maxLength: 25,
                }}
                InputProps={{
                  style: {
                    borderRadius: '8px',
                  },
                }}
                sx={{
                  marginBottom: '16px',
                }}
              />
            ))}
          </Box>
        </Box>
        <Button
          sx={{
            color: '#646D7A',
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '-0.43px',
            textTransform: 'none',
            marginTop: '0px',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            display: { xxs: 'none', sm: 'flex' },
          }}
          onClick={handleAddMoreInterests}
          id="inviteYourContactsAddMore"
        >
          <Add
            sx={{ color: '#646D7A', fontSize: '18px', marginRight: '5px' }}
          />
          <Typography
            sx={{
              color: '#646D7A',
              fontSize: '13px',
              fontWeight: 500,
              lineHeight: '24px',
              letterSpacing: '-0.43px',
            }}
          >
            Add more
          </Typography>
        </Button>
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleAdd}
          disabled={isInviteButtonDisabled}
          sx={{
            ...classes.mainButton,
            color: '#ffffff',
            backgroundColor: '#2B71F6',
            '&:hover': {
              backgroundColor: '#2B71F6',
            },
          }}
        >
          Add Interests
        </Button>
      </DialogActions>
    </Dialog>
  );
}
